// ── Local-first interior layout + batched backend sync ──
//
// The interior editor owns the layout locally: every edit (drag, mirror, unplace,
// undo/redo, wall height…) is applied to the ['houseFurniture', id] cache in ONE
// synchronous write and queued here. The queue is flushed to the backend in a
// single atomic request (PUT /interior-objects/{id}/furniture-layout) a moment
// after the editing settles, instead of one awaited request per item per action.
//
// Why it matters:
//   - no network round-trip sits between the gesture and the settled scene (no
//     micro-lag after a drop);
//   - a table and the items standing on it change in the same cache write, so the
//     undo history can never capture "table moved, vase not yet" as its own step;
//   - the backend applies the batch in one transaction — all of it lands or none.
//
// Consistency rules:
//   - Patches for the same item merge (newest field wins), so N moves of one item
//     cost one row in the next batch.
//   - Any read of the server layout goes through flushLayout() first (see
//     useRoomData / fetchHouseFurniture), so a refetch never shows a layout older
//     than what the player already sees.
//   - A batch the server rejects (4xx) is dropped and the room resyncs from the
//     server; network/5xx failures are retried with backoff.
//   - Leaving the room, hiding the app or closing the page flushes immediately
//     (keepalive request on page hide).
import type { QueryClient } from '@tanstack/react-query';
import type { HouseFurniture } from '../../../models/apiTypes';
import type { PlacementMode } from '../interaction/placement';
import { applyFurnitureLayout } from '../furnitureApi';
import { getHouseFurnitureInteriorObjectsInteriorObjectIdFurnitureGet } from '../../../client';
import { isApiErrorException } from '../../../utils/net/apiError';
import { isPracticeRoom } from '../../../domain/interiorTutorial';

/** Layout fields of one furniture instance; omitted fields are left unchanged. */
export interface LayoutPatch {
  id: number;
  is_placed?: boolean;
  room_index?: number;
  x?: number | null;
  y?: number | null;
  is_mirrored?: boolean;
  placement_mode?: PlacementMode;
  rug_z_order?: number;
  wall_floor_offset?: number;
}

/** Quiet period after the last edit before the batch is sent. */
const FLUSH_DELAY_MS = 1200;
/** Upper bound on how long an edit may wait while the player keeps editing. */
const FLUSH_MAX_WAIT_MS = 5000;
const RETRY_DELAYS_MS = [1000, 3000, 10000];

interface RoomQueue {
  pending: Map<number, LayoutPatch>;
  inFlight: Promise<void> | null;
  timer: ReturnType<typeof setTimeout> | null;
  firstStagedAt: number | null;
  failures: number;
  queryClient: QueryClient | null;
}

const rooms = new Map<number, RoomQueue>();

function roomQueue(mapObjectId: number): RoomQueue {
  let q = rooms.get(mapObjectId);
  if (!q) {
    q = { pending: new Map(), inFlight: null, timer: null, firstStagedAt: null, failures: 0, queryClient: null };
    rooms.set(mapObjectId, q);
  }
  return q;
}

function mergePatch(into: Map<number, LayoutPatch>, patch: LayoutPatch): void {
  const prev = into.get(patch.id);
  into.set(patch.id, prev ? { ...prev, ...patch } : { ...patch });
}

function applyPatchToItem(item: HouseFurniture, patch: LayoutPatch): HouseFurniture {
  const { id: _id, ...fields } = patch;
  const next = { ...item, ...fields } as HouseFurniture;
  if (patch.is_placed === false) {
    next.x = null;
    next.y = null;
  }
  return next;
}

/**
 * Apply layout changes locally (one cache write for all of them) and queue them
 * for the backend. Never throws and never waits on the network.
 */
export function stageLayout(queryClient: QueryClient, mapObjectId: number, patches: LayoutPatch[]): void {
  if (patches.length === 0) return;
  const byId = new Map<number, LayoutPatch>();
  for (const p of patches) mergePatch(byId, p);

  queryClient.setQueryData<HouseFurniture[]>(['houseFurniture', mapObjectId], (old = []) =>
    old.map((f) => {
      const p = byId.get(f.id);
      return p ? applyPatchToItem(f, p) : f;
    }),
  );

  // The tutorial's practice room exists only in this browser — nothing to send.
  if (isPracticeRoom(mapObjectId)) return;

  const q = roomQueue(mapObjectId);
  q.queryClient = queryClient;
  for (const p of byId.values()) mergePatch(q.pending, p);
  scheduleFlush(mapObjectId, q);
}

function scheduleFlush(mapObjectId: number, q: RoomQueue, delay = FLUSH_DELAY_MS): void {
  const now = Date.now();
  if (q.firstStagedAt === null) q.firstStagedAt = now;
  const wait = Math.max(0, Math.min(delay, q.firstStagedAt + FLUSH_MAX_WAIT_MS - now));
  if (q.timer) clearTimeout(q.timer);
  q.timer = setTimeout(() => {
    q.timer = null;
    void flushLayout(mapObjectId);
  }, wait);
}

/** Wire format: an unplace only needs the flag; nulls are never sent. */
function toWireItem(p: LayoutPatch) {
  if (p.is_placed === false) return { id: p.id, is_placed: false };
  const item: Record<string, unknown> = { id: p.id };
  for (const [k, v] of Object.entries(p)) {
    if (k !== 'id' && v !== null && v !== undefined) item[k] = v;
  }
  return item as { id: number };
}

function isPermanentRejection(err: unknown): boolean {
  if (!isApiErrorException(err)) return false;
  const status = err.apiError.status;
  return status >= 400 && status < 500 && status !== 408 && status !== 429;
}

async function sendBatch(mapObjectId: number, q: RoomQueue, keepalive: boolean): Promise<void> {
  if (q.timer) {
    clearTimeout(q.timer);
    q.timer = null;
  }
  if (q.pending.size === 0) return;
  const batch = q.pending;
  q.pending = new Map();
  q.firstStagedAt = null;
  try {
    await applyFurnitureLayout(mapObjectId, Array.from(batch.values()).map(toWireItem), keepalive);
    q.failures = 0;
  } catch (err) {
    if (isPermanentRejection(err)) {
      // The server refused this layout (e.g. the item was sold or the house changed
      // hands). Drop it and show the server's truth instead of retrying forever.
      console.error('[layoutSync] batch rejected, resyncing room', mapObjectId, err);
      q.failures = 0;
      void q.queryClient?.invalidateQueries({ queryKey: ['houseFurniture', mapObjectId] });
      return;
    }
    // Transient: put the batch back UNDER anything staged meanwhile (newer wins).
    const merged = new Map(batch);
    for (const p of q.pending.values()) mergePatch(merged, p);
    q.pending = merged;
    const delay = RETRY_DELAYS_MS[Math.min(q.failures, RETRY_DELAYS_MS.length - 1)];
    q.failures += 1;
    console.warn(`[layoutSync] flush failed, retry in ${delay}ms`, err);
    q.firstStagedAt = null;
    scheduleFlush(mapObjectId, q, delay);
  }
}

/**
 * Send everything queued for this room now and resolve once the backend has it
 * (or the attempt failed — failures are handled internally). Safe to call any time.
 */
export async function flushLayout(mapObjectId: number, opts: { keepalive?: boolean } = {}): Promise<void> {
  const q = rooms.get(mapObjectId);
  if (!q) return;
  // One request at a time per room: chain behind the one in flight, then send
  // whatever accumulated meanwhile.
  while (q.inFlight) await q.inFlight;
  if (q.pending.size === 0) return;
  const run = sendBatch(mapObjectId, q, opts.keepalive ?? false).finally(() => {
    q.inFlight = null;
  });
  q.inFlight = run;
  await run;
}

/**
 * The ONLY way to read a house's furniture from the server: flushes this room's
 * queued edits first, so the response can never be older than the local layout.
 */
export async function fetchHouseFurniture(mapObjectId: number): Promise<HouseFurniture[]> {
  await flushLayout(mapObjectId);
  const { data } = await getHouseFurnitureInteriorObjectsInteriorObjectIdFurnitureGet({
    path: { interior_object_id: mapObjectId },
    throwOnError: true,
  });
  return data.items;
}

export function hasPendingLayout(mapObjectId: number): boolean {
  const q = rooms.get(mapObjectId);
  return !!q && (q.pending.size > 0 || q.inFlight !== null);
}

function flushAllRooms(keepalive: boolean): void {
  for (const id of rooms.keys()) void flushLayout(id, { keepalive });
}

if (typeof window !== 'undefined') {
  // Telegram can kill a hidden mini-app at any moment: don't sit on unsaved edits.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushAllRooms(true);
  });
  window.addEventListener('pagehide', () => flushAllRooms(true));
}
